import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Users, CheckCircle, Clock, XCircle, AlertCircle, Search, ChevronRight, Plus, Upload, X, Ban, CreditCard, Check } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGetDealers, adminGetDealerStats } from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft:          'text-zinc-500 bg-zinc-800 border-zinc-700',
  submitted:      'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  under_review:   'text-blue-400 bg-blue-500/10 border-blue-500/20',
  approved:       'text-green-400 bg-green-500/10 border-green-500/20',
  rejected:       'text-red-400 bg-red-500/10 border-red-500/20',
  info_requested: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  suspended:      'text-red-500 bg-red-900/20 border-red-900/40',
};

// ── Inline Payment Rule Selector ──────────────────────────────────────────────
function InlinePaymentRule({ dealer, rules, token, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const current = dealer.payment_rule_id || '';

  const handleChange = async (ruleId) => {
    setSaving(true);
    try {
      await fetch(`${BASE}/api/admin/dealers/${dealer._id}/payment-rule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ payment_rule_id: ruleId || null }),
      });
      onUpdated();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-1.5 min-w-[180px]">
      <select
        data-testid={`payment-rule-select-${dealer._id}`}
        value={current}
        onChange={e => handleChange(e.target.value)}
        disabled={saving}
        title="Assign payment rule to this dealer"
        className={`flex-1 text-[11px] font-inter py-1.5 px-2 border outline-none transition-colors bg-[#1e1e1e] ${
          current ? 'text-orange-400 border-orange-500/30' : 'text-zinc-500 border-zinc-700'
        } ${saving ? 'opacity-60' : ''}`}
        style={{ maxWidth: 200 }}
      >
        <option value="">— Default (Full Upfront) —</option>
        {rules.map(r => (
          <option key={r.rule_id} value={r.rule_id}>{r.name}</option>
        ))}
      </select>
      {saved && <Check size={12} className="text-green-400 flex-shrink-0" />}
      {saving && <div className="w-3 h-3 border border-orange-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />}
    </div>
  );
}

const inputCls = "w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors placeholder-zinc-600";
const labelCls = "block font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1.5";

// ── Add Dealer Modal ──────────────────────────────────────────────────────────
function AddDealerModal({ onClose, onSaved, token }) {
  const [form, setForm] = useState({
    business_name: '', first_name: '', last_name: '', email: '', phone: '',
    ffl_number: '', ffl_expiry: '', sot_status: 'no', status: 'approved',
    business_address: '', city: '', state: 'TX', zip_code: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.email || !form.business_name) { setError('Business name and email are required'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${BASE}/api/admin/dealers/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create dealer');
      onSaved(data);
    } catch (err) { setError(err.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#111] z-10">
          <h2 className="font-bebas text-xl text-white tracking-wider">ADD NEW DEALER</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Business Name *</label>
              <input required value={form.business_name} onChange={set('business_name')} className={inputCls} placeholder="Smith's Tactical Supply" />
            </div>
            <div><label className={labelCls}>First Name</label><input value={form.first_name} onChange={set('first_name')} className={inputCls} placeholder="John" /></div>
            <div><label className={labelCls}>Last Name</label><input value={form.last_name} onChange={set('last_name')} className={inputCls} placeholder="Smith" /></div>
            <div className="col-span-2">
              <label className={labelCls}>Email Address *</label>
              <input required type="email" value={form.email} onChange={set('email')} className={inputCls} placeholder="dealer@gunshop.com" />
            </div>
            <div><label className={labelCls}>Phone</label><input value={form.phone} onChange={set('phone')} className={inputCls} placeholder="555-867-5309" /></div>
            <div>
              <label className={labelCls}>Account Status</label>
              <select value={form.status} onChange={set('status')} className={inputCls}>
                <option value="approved">Approved (can order immediately)</option>
                <option value="submitted">Submitted (needs review)</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>FFL Number</label>
              <input value={form.ffl_number} onChange={set('ffl_number')} className={inputCls} placeholder="1-TX-XXX-XX-XX-XXXXX" />
            </div>
            <div>
              <label className={labelCls}>FFL Expiry</label>
              <input type="date" value={form.ffl_expiry} onChange={set('ffl_expiry')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>SOT Status</label>
              <select value={form.sot_status} onChange={set('sot_status')} className={inputCls}>
                <option value="no">No SOT</option>
                <option value="yes">Yes — SOT Holder (Class III)</option>
              </select>
            </div>
            <div><label className={labelCls}>State</label><input value={form.state} onChange={set('state')} className={inputCls} placeholder="TX" maxLength={2} /></div>
            <div className="col-span-2">
              <label className={labelCls}>Business Address</label>
              <input value={form.business_address} onChange={set('business_address')} className={inputCls} placeholder="123 Range Road" />
            </div>
          </div>

          <div className="bg-orange-500/10 border border-orange-500/20 p-3">
            <p className="font-inter text-xs text-orange-300">
              A temporary password of <strong>WittDealer2025!</strong> will be set. Tell the dealer to change it after first login.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-lg tracking-widest py-3 transition-colors">
              {saving ? 'CREATING...' : 'CREATE DEALER'}
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-lg tracking-widest py-3 transition-colors">
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Import CSV Modal ──────────────────────────────────────────────────────────
function ImportCsvModal({ onClose, onImported, token }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_').replace(/"/g, ''));
      const rows = lines.slice(1, 6).map(line => {
        const vals = line.split(',').map(v => v.trim().replace(/"/g, ''));
        return Object.fromEntries(headers.map((h, i) => [h, vals[i] || '']));
      });
      setPreview(rows);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setImporting(true); setError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${BASE}/api/admin/dealers/import-csv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setResult(data);
      onImported();
    } catch (err) { setError(err.message); }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="font-bebas text-xl text-white tracking-wider">IMPORT DEALERS — CSV</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">{error}</div>}
          {result && (
            <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3">
              ✓ Imported {result.created} dealers · {result.skipped} skipped (already exist) · {result.failed} failed
            </div>
          )}

          {/* Expected format */}
          <div className="bg-[#1a1a1a] border border-white/10 p-4">
            <p className="font-bebas text-sm text-white tracking-wider mb-2">EXPECTED CSV COLUMNS</p>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
              business_name, first_name, last_name, email, phone, ffl_number, ffl_expiry, sot_status, state, city, zip_code
            </p>
            <p className="font-inter text-[10px] text-zinc-600 mt-2">
              Only <strong className="text-zinc-500">business_name</strong> and <strong className="text-zinc-500">email</strong> are required. All other columns are optional.
              Default password: <strong className="text-zinc-400">WittDealer2025!</strong>
            </p>
          </div>

          <div>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={handleFile} className="hidden" />
            <button onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-white/20 hover:border-orange-500/50 text-zinc-400 hover:text-white font-inter text-sm py-6 transition-colors">
              <Upload size={18} />Click to select CSV file
            </button>
          </div>

          {preview.length > 0 && (
            <div>
              <p className="font-inter text-xs text-zinc-500 mb-2">Preview (first {preview.length} rows):</p>
              <div className="overflow-x-auto bg-[#0f0f0f] border border-white/5 p-3">
                {preview.map((row, i) => (
                  <div key={i} className="font-mono text-[10px] text-zinc-400 mb-1">
                    {row.business_name || '?'} · {row.email || '?'} · FFL: {row.ffl_number || '—'} · {row.state || '—'}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={handleImport} disabled={importing || !fileRef.current?.files?.length}
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bebas text-lg tracking-widest py-3 transition-colors">
              {importing ? 'IMPORTING...' : 'IMPORT DEALERS'}
            </button>
            <button onClick={onClose}
              className="flex-1 border border-white/20 text-zinc-400 hover:text-white font-bebas text-lg tracking-widest py-3 transition-colors">
              CLOSE
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminDealers() {
  const { token } = useAdminAuth();
  const [dealers, setDealers] = useState([]);
  const [stats, setStats] = useState(null);
  const [paymentRules, setPaymentRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyEmailSaved, setNotifyEmailSaved] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      adminGetDealers(token, statusFilter),
      adminGetDealerStats(token),
      fetch(`${BASE}/api/admin/payment-rules`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch(`${BASE}/api/admin/store-settings`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : {}),
    ])
      .then(([d, s, rules, settings]) => {
        setDealers(d);
        setStats(s);
        setPaymentRules(Array.isArray(rules) ? rules : []);
        setNotifyEmail(settings?.dealer_notification_email || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const saveNotifyEmail = async () => {
    await fetch(`${BASE}/api/admin/store-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ dealer_notification_email: notifyEmail }),
    });
    setNotifyEmailSaved(true);
    setTimeout(() => setNotifyEmailSaved(false), 3000);
  };

  useEffect(() => { load(); }, [token, statusFilter]);

  const filtered = dealers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.business_name?.toLowerCase().includes(q) ||
      d.email?.toLowerCase().includes(q) ||
      d.ffl_number?.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-dealers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">DEALER ACCOUNTS</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Review, manage, and add dealer accounts</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="import-csv-btn"
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 border border-white/20 hover:border-orange-500/40 text-zinc-400 hover:text-white font-inter text-sm px-4 py-2.5 transition-colors"
          >
            <Upload size={14} />Import CSV
          </button>
          <button
            data-testid="add-dealer-btn"
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bebas text-base tracking-widest px-5 py-2.5 transition-colors"
          >
            <Plus size={16} />ADD DEALER
          </button>
        </div>
      </div>

      {/* Pending applications alert banner */}
      {stats && (stats.submitted || 0) + (stats.info_requested || 0) > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/40 px-5 py-3 flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
            <div>
              <p className="font-bebas text-base text-orange-400 tracking-wider">
                {(stats.submitted || 0) + (stats.info_requested || 0)} DEALER APPLICATION{(stats.submitted || 0) + (stats.info_requested || 0) !== 1 ? 'S' : ''} AWAITING REVIEW
              </p>
              <p className="font-inter text-xs text-zinc-500">
                {stats.submitted || 0} submitted · {stats.info_requested || 0} info requested — click "Submitted" filter below to review
              </p>
            </div>
          </div>
          <button onClick={() => setStatusFilter('submitted')}
            className="font-bebas text-sm tracking-widest text-orange-400 hover:text-white border border-orange-500/30 hover:border-orange-500 px-3 py-1.5 transition-colors whitespace-nowrap ml-4">
            REVIEW NOW →
          </button>
        </div>
      )}

      {/* Notification email setting */}
      <div className="bg-[#1a1a1a] border border-white/10 px-5 py-3 flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-shrink-0">
          <AlertCircle size={14} className="text-orange-500" />
          <span className="font-inter text-xs text-zinc-400 uppercase tracking-wider">New Application Notifications →</span>
        </div>
        <input
          data-testid="notify-email-input"
          type="email"
          value={notifyEmail}
          onChange={e => setNotifyEmail(e.target.value)}
          placeholder="admin@wittmachine.com"
          className="flex-1 min-w-[200px] bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-1.5 outline-none transition-colors placeholder-zinc-600"
        />
        <button
          data-testid="save-notify-email"
          onClick={saveNotifyEmail}
          className={`flex items-center gap-1.5 font-inter text-xs px-3 py-1.5 border transition-colors whitespace-nowrap ${
            notifyEmailSaved ? 'border-green-500/40 bg-green-500/10 text-green-400' : 'border-white/20 hover:border-orange-500/40 text-zinc-400 hover:text-white'
          }`}
        >
          {notifyEmailSaved ? <><Check size={12} />Saved</> : 'Save Email'}
        </button>
        <p className="font-inter text-[10px] text-zinc-600 w-full">
          When a dealer submits an application, an email notification is sent here automatically.
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {[
            { key: 'total',         label: 'Total',     icon: Users,        color: 'text-zinc-400' },
            { key: 'submitted',     label: 'Submitted', icon: Clock,        color: 'text-yellow-400' },
            { key: 'approved',      label: 'Approved',  icon: CheckCircle,  color: 'text-green-400' },
            { key: 'under_review',  label: 'Review',    icon: AlertCircle,  color: 'text-blue-400' },
            { key: 'info_requested',label: 'Needs Info',icon: AlertCircle,  color: 'text-orange-400' },
            { key: 'rejected',      label: 'Rejected',  icon: XCircle,      color: 'text-red-400' },
            { key: 'draft',         label: 'Draft',     icon: Clock,        color: 'text-zinc-500' },
          ].map(s => {
            const Icon = s.icon;
            return (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                className={`bg-[#1a1a1a] border p-3 text-left transition-colors hover:border-orange-500/30 ${statusFilter === s.key ? 'border-orange-500/50' : 'border-white/10'}`}>
                <Icon size={14} className={`${s.color} mb-1`} />
                <div className={`font-bebas text-2xl tracking-wider ${s.color}`}>{stats[s.key] || 0}</div>
                <div className="font-inter text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="dealer-search" type="text" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by business, email, or FFL..."
            className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm pl-9 pr-4 py-2.5 outline-none focus:border-orange-500 transition-colors placeholder-zinc-600"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'submitted', 'approved', 'under_review', 'rejected', 'suspended', 'draft'].map(s => (
            <button key={s} data-testid={`dealer-filter-${s}`} onClick={() => setStatusFilter(s)}
              className={`font-inter text-xs px-3 py-2 border transition-colors uppercase tracking-wider ${statusFilter === s ? 'border-orange-500 bg-orange-500/10 text-orange-400' : 'border-white/10 text-zinc-500 hover:border-white/30'}`}>
              {s === 'under_review' ? 'Review' : s === 'info_requested' ? 'Info' : s}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-white/10 overflow-x-auto">
          <table className="w-full" data-testid="dealers-table">
            <thead>
              <tr className="border-b border-white/10">
                {['Business', 'Contact', 'FFL #', 'Payment Rule', 'Submitted', 'Status', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-inter text-xs text-zinc-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map(d => (
                <tr key={d._id} data-testid={`dealer-row-${d._id}`} className={`hover:bg-white/3 transition-colors ${d.status === 'suspended' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-inter text-sm font-medium text-white">{d.business_name}</p>
                    <p className="font-inter text-xs text-zinc-600">{d.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-inter text-sm text-zinc-300">{d.first_name} {d.last_name}</p>
                    <p className="font-inter text-xs text-zinc-600">{d.phone}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-inter text-xs text-zinc-400 font-mono">{d.ffl_number || '—'}</p>
                    {d.sot_status === 'yes' && <span className="font-inter text-[9px] text-orange-400">SOT ✓</span>}
                  </td>
                  {/* Inline payment rule selector */}
                  <td className="px-4 py-3">
                    <InlinePaymentRule
                      dealer={d}
                      rules={paymentRules}
                      token={token}
                      onUpdated={load}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-inter text-xs text-zinc-500">
                      {d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-inter text-[10px] font-bold px-2 py-1 border uppercase tracking-wider ${STATUS_COLORS[d.status] || 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>
                      {d.status === 'suspended' ? <><Ban size={8} className="inline mr-1" />Suspended</> : d.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/dealers/${d._id}`} data-testid={`view-dealer-${d._id}`}
                      className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-white transition-colors whitespace-nowrap">
                      Review <ChevronRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="font-inter text-sm text-zinc-600">No dealers found</p>
            </div>
          )}
        </div>
      )}

      {showAddModal && (
        <AddDealerModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onSaved={() => { setShowAddModal(false); load(); }}
        />
      )}
      {showImportModal && (
        <ImportCsvModal
          token={token}
          onClose={() => setShowImportModal(false)}
          onImported={load}
        />
      )}
    </div>
  );
}
